import React from 'react';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { dateToStringFormat } from '@common/utils/date';
import DeploymentBall from './DeploymentBall';
import { DeploymentTimelineEntry } from './useDeploymentTimelineState';
import { darken } from '../../../../utils/deployment-timeline/colors';

const BLOCK_HEIGHT = 28;

interface DeploymentBlockProps {
  entry: DeploymentTimelineEntry;
  left: number;
  width: number;
  color: string;
  activeEdge: 'start' | 'end' | null;
  onActivateEdge: (edge: 'start' | 'end') => void;
  onDrag: (edge: 'start' | 'end', clientX: number) => void;
}

export const toDateInputValue = (epochMs: number): DateTime => {
  return DateTime.fromMillis(epochMs);
};

export function isMissionOutsideRange(entry: DeploymentTimelineEntry): boolean {
  const effectiveEnd = entry.end ?? Date.now();
  if (entry.firstMissionDate !== null && entry.firstMissionDate < entry.start) return true;
  if (entry.lastMissionDate !== null && entry.lastMissionDate > effectiveEnd) return true;
  return false;
}

const DeploymentBlock: React.FC<DeploymentBlockProps> = ({
  entry,
  left,
  width,
  color,
  activeEdge,
  onActivateEdge,
  onDrag,
}) => {
  const { t } = useTranslation();
  const outsideRangeWarning = isMissionOutsideRange(entry);

  const startLabel = dateToStringFormat(DateTime.fromMillis(entry.start));
  const endLabel =
    entry.end === null
      ? t('info-label-ongoing')
      : dateToStringFormat(DateTime.fromMillis(entry.end));

  return (
    <Tooltip
      arrow
      title={
        <Box sx={{ py: 0.25 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {entry.organizationName}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block' }}>
            {startLabel} – {endLabel}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block' }}>
            {t('label-deployment-timeline-missions', { count: entry.missionsCount })}
          </Typography>
          {outsideRangeWarning && (
            <Typography variant="caption" sx={{ display: 'block', color: '#FFD27A', mt: 0.25 }}>
              {t('warning-deployment-missions-outside-range')}
            </Typography>
          )}
        </Box>
      }
    >
      <Box
        data-testid={`deployment-block-${entry.id}`}
        sx={(theme) => ({
          position: 'absolute',
          left,
          width,
          top: 0,
          height: BLOCK_HEIGHT,
          borderRadius: 1,
          background: `linear-gradient(180deg, ${color} 0%, ${darken(color, 0.14)} 100%)`,
          border: outsideRangeWarning
            ? `2px dashed ${theme.palette.warning.main}`
            : `1px solid ${darken(color, 0.28)}`,
          boxShadow: 0.5,
          boxSizing: 'border-box',
        })}
      >
        <DeploymentBall
          testId={`deployment-ball-start-${entry.id}`}
          leftPx={0}
          position="top"
          active={activeEdge === 'start'}
          color={color}
          onActivate={() => onActivateEdge('start')}
          onDrag={(clientX) => onDrag('start', clientX)}
        />
        {entry.end !== null && (
          <DeploymentBall
            testId={`deployment-ball-end-${entry.id}`}
            leftPx={width}
            position="bottom"
            active={activeEdge === 'end'}
            color={color}
            onActivate={() => onActivateEdge('end')}
            onDrag={(clientX) => onDrag('end', clientX)}
          />
        )}
      </Box>
    </Tooltip>
  );
};

export default DeploymentBlock;
